// src/services/purchaseReceiptService.js
// SOT: purchase_receipts (Material Receive SOT per Constitution)
// SOT: financial_transactions (Financial Entries SOT per Constitution)
// Constitution: ALL business logic through service layer
// Constitution: UI MUST NEVER write Firestore directly

import {
  collection, addDoc, updateDoc,
  doc, getDoc, serverTimestamp, getDocs,
  query, where
} from "firebase/firestore";
import { db } from "../firebase";

// ─────────────────────────────────────────────────────
// COMPLETE GRN — Full receive (no shorts)
// ─────────────────────────────────────────────────────
export const completePurchaseReceipt = async ({
  purchase_order_id,
  poData,
  updatedItems,
  grandTotal,
  hasOver,
  receipt_type,       // "full" | "short_close"
  short_close_reason, // only for short_close
  completed_by,
  completed_by_name,
  company_id,
  // Actual Freight — optional, entered at GRN once the real transporter/
  // vendor invoice confirms it. Reference only: stored ALONGSIDE the PO's
  // own freight_amount estimate (never overwrites it), and never feeds
  // grand_total, the vendor-payable financial_transactions entry, or any
  // inventory value. 0/undefined means "not provided this GRN".
  actualFreight = 0,
}) => {

  if (!company_id) {
    throw new Error("company_id required");
  }
  if (!purchase_order_id) {
    throw new Error("purchase_order_id required");
  }

  // ── STEP 0: verify the PO actually belongs to this company before
  // any write — company_id here is caller-supplied, not re-derived
  // from the acting user's own token inside this service layer, so
  // this is the only guard against a wrong/forged company_id being
  // used to write against another company's PO (Phase 2 audit) ──
  const poSnap = await getDoc(doc(db, "purchase_orders", purchase_order_id));
  if (!poSnap.exists()) {
    throw new Error("Purchase order not found");
  }
  if (poSnap.data().company_id !== company_id) {
    throw new Error("Access denied — purchase order belongs to a different company");
  }

  // ── STEP 1: purchase_receipts — SOT for Material Receive ──
  // Constitution: Material Receive → purchase_receipts
  const receiptItems = updatedItems.map(item => ({
    ...item,
    // ✅ Rate + amount embedded in receipt — GRN = SOT for amounts
    amount: Number(item.received_qty || 0) * Number(item.rate || 0),
  }));

  const receiptRef = await addDoc(collection(db, "purchase_receipts"), {
    purchase_order_id,
    company_id,
    po_number:            poData.po_number   || "",
    vendor_id:            poData.vendor_id   || "",
    vendor_name:          poData.vendor_name || "",
    receipt_type,                              // "full" | "short_close"
    items:                receiptItems,        // ✅ rate + amount per item
    grand_total:          grandTotal,          // ✅ SOT for payable amount
    // Reference only — never used in any total/payable calculation. Absent
    // (0) means the Manager didn't have the real figure at GRN time.
    actual_freight_amount: Number(actualFreight || 0),
    over_received:        hasOver,
    over_qty:             hasOver
      ? updatedItems.reduce((s, i) => s + Number(i.over_qty || 0), 0)
      : 0,
    short_close:          receipt_type === "short_close",
    short_close_reason:   short_close_reason || "",
    total_cancelled_qty:  updatedItems.reduce((s, i) => s + Number(i.cancelled_qty || 0), 0),
    completed_by,
    completed_by_name,
    created_at:           serverTimestamp(),
  });

  // ── STEP 2: purchase_orders update ──
  // Constitution: Procurement SOT = purchase_orders
  const poStatusAfter = receipt_type === "short_close"
    ? "short_closed"
    : hasOver ? "over_received" : "completed";

  await updateDoc(doc(db, "purchase_orders", purchase_order_id), {
    items:                 updatedItems,
    po_status:             poStatusAfter,
    grn_locked:            true,
    // Actual Freight — reference only, additive. The PO's own
    // freight_amount (set at creation) stays exactly as originally
    // entered; this is a SEPARATE field so both the estimate and the
    // confirmed-actual figure remain visible for comparison afterward.
    // Only written when the Manager actually provided a value at GRN.
    ...(actualFreight > 0 ? { actual_freight_amount: Number(actualFreight) } : {}),
    short_closed:          receipt_type === "short_close",
    short_close_reason:    short_close_reason || "",
    short_closed_by:       receipt_type === "short_close" ? completed_by      : "",
    short_closed_by_name:  receipt_type === "short_close" ? completed_by_name : "",
    short_closed_at:       receipt_type === "short_close" ? serverTimestamp()  : null,
    over_received:         hasOver,
    over_qty:              hasOver
      ? updatedItems.reduce((s, i) => s + Number(i.over_qty || 0), 0)
      : 0,
    total_cancelled_qty:   updatedItems.reduce((s, i) => s + Number(i.cancelled_qty || 0), 0),
    grand_total:           grandTotal,
    purchase_receipt_id:   receiptRef.id,      // ✅ link to receipt SOT
    grn_completed_by:      completed_by,
    grn_completed_by_name: completed_by_name,
    grn_completed_at:      serverTimestamp(),
    updated_at:            serverTimestamp(),
    updated_by:            completed_by,
  });

  // ── STEP 3: financial_transactions — SOT for Financial Entries ──
  // Constitution: Financial Entries → financial_transactions
  // vendor_payments collection = WRONG → financial_transactions = CORRECT
  if (grandTotal > 0) {
    await addDoc(collection(db, "financial_transactions"), {
      company_id,
      type:                 "vendor",
      sub_type:             "po_payment",
      category:             "material",          // ✅ NEW — classifies for Accounts Dashboard
      reference_type:       "po_grn",             // ✅ NEW — consistent with other flows' naming
      vendor_id:            poData.vendor_id   || "",
      vendor_name:          poData.vendor_name || "",
      purchase_order_id,
      po_number:            poData.po_number   || "",
      purchase_receipt_id:  receiptRef.id,      // ✅ SOT link
      amount:               grandTotal,          // ✅ from receipt SOT
      paid_amount:          0,
      balance:              grandTotal,
      status:               "unpaid",
      payment_mode:         "",
      reference_id:         receiptRef.id,
      reference_no:         poData.po_number   || "",
      receipt_type,
      short_close_reason:   short_close_reason || "",
      created_at:           serverTimestamp(),
      updated_at:           serverTimestamp(),
      created_by:           completed_by,
      created_by_name:      completed_by_name,
    });
  }

  // ── STEP 4: MRF notify — procurement_pending → approved_pending_issue ──
  await notifyMRFsAfterReceipt(
    updatedItems
      .filter(i => Number(i.received_qty || 0) > 0)
      .map(i => String(i.item_id || "").trim())
      .filter(Boolean),
    company_id
  );

  return { success: true, receipt_id: receiptRef.id };
};

// ─────────────────────────────────────────────────────
// MRF NOTIFY — procurement_pending → approved_pending_issue
// ─────────────────────────────────────────────────────
const notifyMRFsAfterReceipt = async (receivedItemIds, companyId) => {
  try {
    const mrfSnap = await getDocs(query(
      collection(db, "material_requests"),
      where("company_id", "==", companyId),
      where("status", "in", ["approved", "partially_issued"])
    ));

    for (const mrfDoc of mrfSnap.docs) {
      const mrfData  = mrfDoc.data();
      const mrfItems = Array.isArray(mrfData.items) ? [...mrfData.items] : [];
      let   updated  = false;

      const newItems = mrfItems.map(item => {
        if (item.item_status !== "procurement_pending") return item;
        const itemId = String(item.item_id || "").trim();
        if (!itemId || !receivedItemIds.includes(itemId)) return item;
        updated = true;
        return {
          ...item,
          item_status:               "approved_pending_issue",
          stock_available_after_grn: true,
        };
      });

      if (updated) {
        await updateDoc(doc(db, "material_requests", mrfDoc.id), {
          items:      newItems,
          updated_at: serverTimestamp(),
        });
      }
    }
  } catch (err) {
    console.error("⚠ MRF notify failed (non-blocking):", err);
  }
};